import {type} from 'arktype'
import {klona} from 'klona'

import {isStateProvinceRequiredCountry} from '@/lib/flight/constants'
import {
  type AssistanceInformation,
  BookingType,
  DEFAULT_ASSISTANCE_INFORMATION,
  Gender,
  PassengerType,
  type RawTimesFilter,
  type TimesFilter,
  TravelClass,
} from '@/lib/flight/types'
import {calculateAge} from '@/lib/flight/utils'
import {COUNTRY_CODES} from '@/lib/locale/country'
import {isStateProvinceOf} from '@/lib/locale/regions'
import filteredArray from '@/utils/schema/filteredArray'

export const Airport = type({
  id: 'string',
  name: 'string',
  city: 'string',
})
export type Airport = typeof Airport.infer

export const AirportWithTime = type({
  '...': Airport,
  'time': 'string',
})
export type AirportWithTime = typeof AirportWithTime.infer

export const LayoverAirport = type({
  '...': Airport,
  'duration': 'number',
  'overnight': 'boolean?',
})
export type LayoverAirport = typeof LayoverAirport.infer

export const Flight = type({
  airline: 'string',
  airline_logo: 'string',
  airplane: 'string?',
  arrival_airport: AirportWithTime,
  departure_airport: AirportWithTime,
  duration: 'number',
  extensions: 'string[]?',
  flight_number: 'string',
  legroom: 'string?',
  travel_class: 'string',
  ticket_also_sold_by: 'string[]?',
  overnight: 'boolean?',
})
export type Flight = typeof Flight.infer

export function parseTravelClass(travelClass: unknown): TravelClass {
  const travelClassString = String(travelClass)
  if (travelClassString === '1') return TravelClass.Economy
  if (travelClassString === '2') return TravelClass.PremiumEconomy
  if (travelClassString === '3') return TravelClass.Business
  if (travelClassString === '4') return TravelClass.FirstClass
  return TravelClass.Economy
}

export function parseBookingType(bookingType: unknown): BookingType {
  const bookingTypeString = String(bookingType)
  if (bookingTypeString === 'oneway') return BookingType.OneWay
  if (bookingTypeString === 'roundtrip') return BookingType.RoundTrip
  return BookingType.OneWay
}

export const FlightResultItem = type({
  departure_token: 'string?', // For every flight except the last one
  flight_id: 'string?', // For the last flight
  price: 'number',
  type: 'string',
  airline_logo: 'string',
  flights: Flight.array(),
  layovers: LayoverAirport.array().optional(),
  total_duration: 'number',
})
export type FlightResultItem = typeof FlightResultItem.infer

export const FlightResult = type({
  flights: {
    best_flights: filteredArray(FlightResultItem),
    other_flights: filteredArray(FlightResultItem),
  },
  previous_segments: filteredArray(FlightResultItem).optional(),
  metadata: {
    'thread-id': '(string | null)?',
  },
  suggested_prompts: 'string[]',
})
export type FlightResult = typeof FlightResult.infer

//------------------------------------------------------------------------------

export const CarbonEmissions = type({
  difference_percent: 'number?',
  this_flight: 'number?',
  typical_for_this_route: 'number?',
})
export type CarbonEmissions = typeof CarbonEmissions.infer

export const SelectedFlight = type({
  airline_logo: 'string?',
  carbon_emissions: CarbonEmissions.optional(),
  flights: Flight.array(),
  layovers: LayoverAirport.array().optional(),
  total_duration: 'number',
  type: 'string',
})
export type SelectedFlight = typeof SelectedFlight.infer

export const Metadata = type({
  arrival_id: 'string',
  arrival_city: 'string',
  booking_token: 'string',
  currency: 'string',
  departure_id: 'string',
  departure_city: 'string',
  engine: 'string',
  flight_id: 'string',
  gl: 'string',
  google_flights_url: 'string',
  hl: 'string',
  outbound_date: 'string',
  return_date: 'string?',
  adults: 'number',
  children: 'number',
  infants_in_seat: 'number',
  infants_on_lap: 'number',
  travel_class: 'number',
  type: 'string',
  thread_id: '(string | null)?',
})
export type Metadata = typeof Metadata.infer

export const BaggagePrices = type({
  departing: 'string[]?',
  returning: 'string[]?',
  together: 'string[]?',
})
export type BaggagePrices = typeof BaggagePrices.infer

export const BookingRequest = type({
  post_data: 'string',
  url: 'string',
})
export type BookingRequest = typeof BookingRequest.infer

export const LocalPrice = type({
  currency: 'string',
  price: 'number',
})
export type LocalPrice = typeof LocalPrice.infer

export const BookingOptionItem = type({
  airline_logos: 'string[]',
  baggage_prices: 'string[]?',
  extensions: 'string[]?',
  book_with: 'string',
  booking_request: BookingRequest.optional(),
  marketed_as: 'string[]',
  local_prices: LocalPrice.array().optional(),
  price: type('number'),
  // price: type('number').pipe.try(value => value + 0.5), // To test the roundup feature, since all flight price are all rounded
})
export type BookingOptionItem = typeof BookingOptionItem.infer

export const BookingOption = type({
  departing: BookingOptionItem.optional(),
  returning: BookingOptionItem.optional(),
  together: BookingOptionItem.optional(),
  option_title: 'string?',
  flight_id: 'string?',
  separate_tickets: 'boolean?',
})
export type BookingOption = typeof BookingOption.infer

export const FlightDetails = type({
  baggage_prices: BaggagePrices.optional(),
  booking_options: BookingOption.array(),
  metadata: Metadata,
  selected_flights: SelectedFlight.array(),
  suggested_prompts: 'string[]?',
})
export type FlightDetails = typeof FlightDetails.infer

const countrySchema = type.enumerated(...COUNTRY_CODES).configure({
  message: 'Country is required',
})
export type PaxCountry = typeof countrySchema.infer

export const Passenger = type({
  type: type.enumerated(...Object.values(PassengerType)).configure({
    message: 'Passenger type is required',
  }),
  lastName: type('string > 0').configure({
    message: 'Last name is required',
  }),
  middleName: `string = ''`,
  firstName: type('string > 0').configure({
    message: 'First name is required',
  }),
  dateOfBirth: type(`Date < ${Date.now()}`).configure({
    message: ctx => {
      if (ctx.code === 'required') return 'Date of birth is required'
      if (ctx.code === 'before') return 'Date of birth cannot be in the future'
      return 'Date of birth is not valid'
    },
  }),
  gender: type.enumerated(...Object.values(Gender)).configure({
    message: 'Gender is required',
  }),
  country: countrySchema,
  stateProvince: type('string'),
}).narrow((passenger, ctx) => {
  if (isStateProvinceRequiredCountry(passenger.country)) {
    if (!passenger.stateProvince.trim()) {
      return ctx.reject({
        relativePath: ['stateProvince'],
        message: 'State / Province is required',
      })
    }

    if (!isStateProvinceOf(passenger.country, passenger.stateProvince)) {
      return ctx.reject({
        relativePath: ['stateProvince'],
        message: 'Invalid state / province',
      })
    }

    return true
  }

  const age = calculateAge(passenger.dateOfBirth)

  let message = ''

  switch (passenger.type) {
    case PassengerType.Adult:
      if (age < 12) {
        message = 'Adults must be at least 12 years old'
      }
      break
    case PassengerType.Child:
      if (age >= 12 || age < 2) {
        message = 'Children must be between 2 and 12 years old'
      }
      break
    case PassengerType.InfantInSeat:
    case PassengerType.InfantOnLap:
      if (age > 2) {
        message = 'Infants must be less than 2 years old'
      }
      break
  }

  if (message) {
    return ctx.reject({
      relativePath: ['dateOfBirth'],
      message,
    })
  }

  return true
})
export type Passenger = typeof Passenger.infer

export function getDefaultPassenger() {
  return {
    type: PassengerType.Adult,
    dateOfBirth: new Date(),
    firstName: '',
    gender: Gender.Male,
    lastName: '',
    middleName: '',
    country: '',
    stateProvince: '',
  }
}

export const FlightBooking = type({
  passengers: Passenger.array(),
  contact: {
    email: type('string.email').configure({
      message: 'Email is not valid',
    }),
    phone: type('string.digits > 0').configure({
      message: 'Phone is not valid',
    }),
    lastName: type('string > 0').configure({
      message: 'Last name is required',
    }),
    middleName: type('string').default(''),
    firstName: type('string > 0').configure({
      message: 'First name is required',
    }),
  },
})

export type FlightBooking = typeof FlightBooking.infer
export const DEFAULT_FLIGHT_BOOKING: FlightBooking = {
  passengers: [],
  contact: {
    email: '',
    firstName: '',
    lastName: '',
    middleName: '',
    phone: '',
  },
}
export function getDefaultFlightBooking() {
  return klona(DEFAULT_FLIGHT_BOOKING)
}

export function getDefaultAssistanceInformation(): AssistanceInformation {
  return klona(DEFAULT_ASSISTANCE_INFORMATION)
}

function parseOrDefault<T>(value: unknown, parser: (value: unknown) => T, defaultValue: T): T {
  try {
    return parser(value)
  } catch {
    return defaultValue
  }
}

function parseTime(value: unknown): number {
  const fromNumber = Math.floor(Number(value))
  if (fromNumber < 0 || fromNumber > 23) {
    throw new Error('Invalid departure from time')
  }
  return fromNumber
}

export function parseTimesFilter(timesFilter: unknown): TimesFilter {
  const timesFilterString = String(timesFilter)
  const [departureFrom, departureTo, arrivalFrom, arrivalTo] = timesFilterString
    .split(',')
    .map(Number)
    .map(Math.floor)

  const parsedDepartureFrom = parseOrDefault(departureFrom, parseTime, 0)
  const parsedDepartureTo = parseOrDefault(departureTo, parseTime, 23)
  const parsedArrivalFrom = parseOrDefault(arrivalFrom, parseTime, 0)
  const parsedArrivalTo = parseOrDefault(arrivalTo, parseTime, 23)

  return {
    departure: {
      from: parsedDepartureFrom,
      to: parsedDepartureTo,
    },
    arrival: {
      from: parsedArrivalFrom,
      to: parsedArrivalTo,
    },
  }
}

export function timesFilterToRawTimesFilter(timesFilter: TimesFilter): RawTimesFilter {
  const departureFrom = parseOrDefault(timesFilter.departure.from, parseTime, 0)
  const departureTo = parseOrDefault(timesFilter.departure.to, parseTime, 23)
  const arrivalFrom = timesFilter.arrival.from
    ? parseOrDefault(timesFilter.arrival.from, parseTime, 0)
    : undefined
  const arrivalTo = timesFilter.arrival.to
    ? parseOrDefault(timesFilter.arrival.to, parseTime, 23)
    : undefined

  if (arrivalFrom === undefined || arrivalTo === undefined) {
    return `${departureFrom},${departureTo}`
  }

  return `${departureFrom},${departureTo},${arrivalFrom},${arrivalTo}`
}

const PaxTicketNumber = type({
  paxId: 'string',
  ticketNumber: 'string?',
})

const BookedFlight = type({
  '...': SelectedFlight,
  'pnr': 'string?',
  'paxTicketNumbers': PaxTicketNumber.array().optional(),
})

// Migrate to use `Passenger` above, note that country and state/province may be validate differently:
const ResPassenger = type({
  id: 'string',
  firstName: 'string',
  lastName: 'string',
  dateOfBirth: 'string',
  gender: 'string',
  type: 'string?',
  country: 'string?',
  stateProvince: 'string?',
})

export const PaymentInfo = type({
  checkoutSessionId: 'string?',
})

// duplicated?
export const ContactInfo = type({
  firstName: 'string',
  lastName: 'string',
  phoneNumber: 'string',
  email: 'string',
})

export const OptionContent = type({
  baggage_prices: 'string[]?',
  book_with: 'string',
  extensions: 'string[]?',
  price: 'string.numeric',
})

export type OptionContent = typeof OptionContent.infer

export const Option = type({
  flight_id: 'string',
  option_title: 'string',
  departing: OptionContent.optional(),
  returning: OptionContent.optional(),
  together: OptionContent.optional(),
})
export type Option = typeof Option.infer

export const MoreInfo = type({
  amountDiscount: 'string',
  amountRoundUp: 'string',
  baggagePrices: 'string[]?',
  bookWith: 'string?',
  extensions: 'string[]?',
  optionTitle: 'string?',
  anonymous: 'boolean',
  option: Option.optional(),
})

export enum BookingStatus {
  AWAITING_PAYMENT = 'AWAITING_PAYMENT',
  FAILED = 'FAILED',
  CONFIRMED = 'CONFIRMED',
  TICKET_ISSUED = 'TICKET_ISSUED',
  COMPLETED = 'COMPLETED',
  CANCELLATION_REQUESTED = 'CANCELLATION_REQUESTED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export const Booking = type({
  id: 'string',
  accountId: 'string?',
  email: 'string',
  status: type.enumerated(...Object.values(BookingStatus)),
  checkoutType: 'string',
  flightId: 'string?',
  price: 'string',
  currency: 'string',
  flightSegments: BookedFlight.array(),
  passengers: ResPassenger.array(),
  paymentInfo: PaymentInfo,
  contactInfo: ContactInfo,
  locale: 'string',
  moreInfo: MoreInfo,
  createdAt: 'string',
  updatedAt: 'string',
  createdBy: 'string',
  updatedBy: 'string',
})
export type Booking = typeof Booking.infer

export const Bookings = filteredArray(Booking)
export type Bookings = typeof Bookings.infer

export const FlightPayment = type({
  clientSecret: 'string',
  booking: Booking,
  paymentIntentStatus: 'string',
})

export type FlightPayment = typeof FlightPayment.inferOut
